// Presentation reducer: applies an event to the *presented* view so HUD numbers change exactly when
// an animation lands ("commit on arrival", docs/06 §5.1). It only needs to be right for what the HUD
// shows; after each batch the presented view is replaced by the authoritative one (reconciliation).
import { COLORS, type GameEvent, type PlayerView } from '@bcm/rules';
import { C } from './content';

export function applyEventToView(v: PlayerView, e: GameEvent): PlayerView {
  const seat = 'seat' in e && typeof e.seat === 'number' ? v.seats[e.seat] : undefined;
  switch (e.t) {
    case 'PermitsGained':
      if (seat) for (const c of COLORS) seat.permits[c] += e.permits[c] ?? 0;
      break;
    case 'PermitsSpent':
      if (seat) for (const c of COLORS) seat.permits[c] -= e.permits[c] ?? 0;
      break;
    case 'ManaGained':
      if (seat) seat.mana += e.amount;
      break;
    case 'ManaSpent':
      if (seat) seat.mana -= e.amount;
      break;
    case 'VPGained':
      if (seat) seat.vp += e.amount;
      break;
    case 'RankPromoted':
      if (seat) seat.rank = e.rank;
      break;
    case 'SpellCast':
      if (seat) {
        for (const c of COLORS) seat.permits[c] -= e.spent[c] ?? 0;
        seat.mana -= e.mana;
        seat.spellHandCount -= 1;
        if (seat.spellHand) seat.spellHand = seat.spellHand.filter((x) => x !== e.card);
        seat.tableau = [...seat.tableau, e.card];
        v.cards[e.card] = e.def;
      }
      break;
    case 'SpellPurchased':
      if (seat) {
        v.arcanium = v.arcanium.map((x) => (x === e.card ? null : x));
        seat.spellHandCount += 1;
        if (seat.spellHand) seat.spellHand = [...seat.spellHand, e.card];
        v.cards[e.card] = e.def;
      }
      break;
    case 'ArcaniumRefilled':
      v.spellDeckCount = Math.max(0, v.spellDeckCount - 1);
      v.arcanium = v.arcanium.map((x, i) => (i === e.slot ? e.card : x));
      v.cards[e.card] = e.def;
      break;
    case 'ArcaniumCleared':
      v.arcanium = v.arcanium.map(() => null);
      // The cleared spells go to the (public) spell discard, where Lost Property can find them.
      v.spellDiscard = [...v.spellDiscard, ...e.cards.map((c) => c.card)];
      for (const c of e.cards) v.cards[c.card] = c.def;
      break;
    case 'DeckShuffled':
      if (e.deck === 'action') {
        v.actionDeckCount = e.size;
        v.actionDiscard = [];
      } else if (e.deck === 'decree') {
        v.decreeDeckCount = e.size;
        v.decreeDiscard = [];
      } else {
        v.spellDeckCount = e.size;
        v.spellDiscard = [];
      }
      break;
    case 'CardDrawn':
      if (e.deck === 'action') v.actionDeckCount = Math.max(0, v.actionDeckCount - 1);
      else v.spellDeckCount = Math.max(0, v.spellDeckCount - 1);
      if (seat) {
        if (e.deck === 'action') {
          seat.actionHandCount += 1;
          if (seat.actionHand && e.def) seat.actionHand = [...seat.actionHand, e.card];
        } else {
          seat.spellHandCount += 1;
          if (seat.spellHand && e.def) seat.spellHand = [...seat.spellHand, e.card];
        }
        if (e.def) v.cards[e.card] = e.def;
      }
      break;
    case 'CardDiscarded':
      if (e.deck === 'action') v.actionDiscard = [...v.actionDiscard, e.card];
      else v.spellDiscard = [...v.spellDiscard, e.card];
      if (seat && e.fromHand && e.deck === 'action') {
        seat.actionHandCount = Math.max(0, seat.actionHandCount - 1);
        if (seat.actionHand) seat.actionHand = seat.actionHand.filter((x) => x !== e.card);
      }
      if (seat && e.fromHand && e.deck === 'spell') {
        // The Shredding Room (P-18) discards spells from hand too.
        seat.spellHandCount = Math.max(0, seat.spellHandCount - 1);
        if (seat.spellHand) seat.spellHand = seat.spellHand.filter((x) => x !== e.card);
      }
      v.cards[e.card] = e.def;
      break;
    case 'SpellSwapped':
      if (seat) {
        v.arcanium = v.arcanium.map((x, i) => (i === e.slot ? e.handCard : x));
        if (seat.spellHand) seat.spellHand = seat.spellHand.map((x) => (x === e.handCard ? e.arcCard : x));
        v.cards[e.handCard] = e.handDef;
        v.cards[e.arcCard] = e.arcDef;
      }
      break;
    case 'ActionCardPlayed':
      if (seat) {
        seat.actionHandCount = Math.max(0, seat.actionHandCount - 1);
        if (seat.actionHand) seat.actionHand = seat.actionHand.filter((x) => x !== e.card);
        v.cards[e.card] = e.def;
      }
      break;
    case 'AssistantPlaced': {
      v.firstPlacementDone = true;
      const loc = v.board[e.location];
      loc.occupants = [...loc.occupants, e.assistant];
      const a = seat?.assistants.find((x) => x.id === e.assistant);
      if (a) {
        a.placed = true;
        a.location = e.location;
      }
      break;
    }
    case 'AssistantGained':
      if (seat) seat.assistants = [...seat.assistants, { id: e.assistant, temp: e.temp, placed: false, location: null, ...(e.intern ? { intern: true } : {}) }];
      break;
    case 'AssistantsRetrieved':
      for (const s of v.seats) {
        s.assistants = s.assistants.filter((a) => !a.temp).map((a) => ({ ...a, placed: false, location: null }));
      }
      for (const loc of Object.values(v.board)) {
        loc.occupants = [];
        loc.blockedBy = [];
      }
      v.modifiers = [];
      break;
    case 'LocationBlocked':
      v.board[e.location].blockedBy = [...v.board[e.location].blockedBy, e.seat];
      break;
    case 'ProblemClaimed':
      if (seat) {
        seat.problems = [...seat.problems, { problem: e.problem, tier: e.tier, vp: e.vp }];
        v.problems = v.problems.map((p) => (p.problem === e.problem ? { ...p, claims: [...p.claims, e.seat] } : p));
      }
      break;
    case 'PermitsExchanged': {
      const other = v.seats[e.target];
      if (seat && other) {
        seat.permits[e.gave] -= 1;
        other.permits[e.took] -= 1;
        seat.permits[e.took] += 1;
        other.permits[e.gave] += 1;
      }
      break;
    }
    case 'DecreeEnacted':
    case 'DecreeIssued': {
      const wasInForce = (v.decrees ?? []).some((d) => d.decree === e.decree);
      const rest = (v.decrees ?? []).filter((d) => d.decree !== e.decree && d.decree !== e.superseded);
      v.decrees = [...rest, { decree: e.decree, by: e.t === 'DecreeEnacted' ? e.seat : null, round: v.round }];
      // It came off the deck, or back from the discard; the superseded one goes to the discard.
      const discard = v.decreeDiscard ?? [];
      if (!wasInForce) {
        if (discard.includes(e.decree)) v.decreeDiscard = discard.filter((d) => d !== e.decree);
        else v.decreeDeckCount = Math.max(0, (v.decreeDeckCount ?? 0) - 1);
      }
      if (e.superseded) v.decreeDiscard = [...(v.decreeDiscard ?? []), e.superseded];
      break;
    }
    case 'DecreeRepealed':
      v.decrees = (v.decrees ?? []).filter((d) => d.decree !== e.decree);
      v.decreeDiscard = [...(v.decreeDiscard ?? []), e.decree];
      break;
    case 'SpellSuspended':
      v.suspended = { ...(v.suspended ?? {}), [e.card]: e.until };
      break;
    case 'SpellReinstated': {
      const { [e.card]: _gone, ...rest } = v.suspended ?? {};
      v.suspended = rest;
      break;
    }
    case 'SpellRevoked': {
      const owner = v.seats[e.owner];
      if (owner) {
        owner.tableau = owner.tableau.filter((x) => x !== e.card);
        owner.spellHandCount += 1;
        if (owner.spellHand) owner.spellHand = [...owner.spellHand, e.card];
      }
      break;
    }
    case 'ManaPaid': {
      const to = v.seats[e.to];
      if (seat) seat.mana -= e.amount;
      if (to) to.mana += e.amount;
      break;
    }
    case 'ActionStolen': {
      const from = v.seats[e.from];
      if (from) {
        from.actionHandCount = Math.max(0, from.actionHandCount - 1);
        if (from.actionHand) from.actionHand = from.actionHand.filter((x) => x !== e.card);
      }
      if (seat) {
        seat.actionHandCount += 1;
        if (seat.actionHand && e.def) seat.actionHand = [...seat.actionHand, e.card];
      }
      if (e.def) v.cards[e.card] = e.def;
      break;
    }
    // ── Resolutions and Annex rooms (P-17, P-18) ──
    case 'ResolutionQueued': {
      const r = v.resolutions ?? { queued: [], inForce: [] };
      v.resolutions = { ...r, queued: [...r.queued, { resolution: e.resolution, problem: e.problem, by: e.seat, round: v.round }] };
      break;
    }
    case 'ResolutionEnacted': {
      const r = v.resolutions ?? { queued: [], inForce: [] };
      v.resolutions = {
        queued: r.queued.filter((q) => q.resolution !== e.resolution),
        inForce: [...r.inForce, { resolution: e.resolution, problem: e.problem, by: e.seat, round: v.round }],
      };
      // Early Closing and Labour Accord move the calendar (as the engine does on enactment).
      for (const op of C.resolutions.get(e.resolution)?.onEnact ?? []) {
        if (op.op === 'setLastRound' && (v.lastRound ?? v.options.rounds) > op.round) v.lastRound = op.round;
        if (op.op === 'setInternRound' && v.internRound != null && v.round + 1 < v.internRound) v.internRound = v.round + 1;
      }
      break;
    }
    case 'AnnexOpened': {
      const a = v.annex ?? { bays: [], opened: [] };
      const bays = [...a.bays];
      bays[e.bay] = e.location;
      v.annex = { bays, opened: [...a.opened.filter((x) => x !== e.replaced), e.location] };
      break;
    }
    case 'HandsRevealed':
      v.openHands = true;
      for (const h of e.hands) {
        const st = v.seats[h.seat];
        if (!st) continue;
        st.spellHand = h.spells.map((x) => x.card);
        st.actionHand = h.actions.map((x) => x.card);
        for (const x of [...h.spells, ...h.actions]) v.cards[x.card] = x.def;
      }
      break;
    case 'ActionCardGiven': {
      const to = v.seats[e.to];
      if (seat) {
        seat.actionHandCount = Math.max(0, seat.actionHandCount - 1);
        if (seat.actionHand) seat.actionHand = seat.actionHand.filter((x) => x !== e.card);
      }
      if (to) {
        to.actionHandCount += 1;
        if (to.actionHand && e.def) to.actionHand = [...to.actionHand, e.card];
      }
      if (e.def) v.cards[e.card] = e.def;
      break;
    }
    case 'SpellRecovered':
      v.spellDiscard = v.spellDiscard.filter((x) => x !== e.card);
      if (seat) {
        seat.spellHandCount += 1;
        if (seat.spellHand) seat.spellHand = [...seat.spellHand, e.card];
      }
      v.cards[e.card] = e.def;
      break;
    case 'ModifierAdded':
      v.modifiers = [...(v.modifiers ?? []), { seat: e.seat, kind: e.kind, amount: e.amount, source: { kind: 'rules' } }];
      break;
    case 'DraftResolved':
      // The draft is over the moment identities are revealed (not at the end of the batch), so the
      // draft screen never lingers over the opening.
      v.phase = 'action';
      e.identities.forEach((id, i) => {
        if (v.seats[i]) v.seats[i]!.identity = id;
      });
      break;
    case 'RoundStarted':
      v.round = e.round;
      v.firstPlacementDone = false;
      break;
    case 'TurnStarted':
      v.activeSeat = e.seat;
      break;
    case 'FirstPlayerPassed':
      v.firstSeat = e.seat;
      v.stampHolder = e.seat;
      break;
    case 'StampTaken':
      v.stampHolder = e.seat;
      break;
    default:
      break;
  }
  return v;
}
